import {L} from "../lt-ng/landcare-toolkit";
import {IContextLayerQuery} from "../utility/geo/context-layer-query";
import {queryFeatures} from "@esri/arcgis-rest-feature-service";
import {IPoint} from "@esri/arcgis-rest-common-types";
import LayerService = L.util.LayerService;
import getLayerServiceType = L.tree.getLayerServiceType;
import SERVICE_TYPES = L.layer.SERVICE_TYPES;
import {LTServices} from "../lt-ng/lt-services";
import IMapService = LTServices.IMapService;
import Item = L.tree.Item;
import {getContextQueryForLayer} from "../utility/geo/helpers";
import {IPortalServices} from "../lt-ng/portal-services";
import {transform} from "ol/proj";
import {PortalsServicesLayerQuery} from "../utility/geo/portals-layer-query";
import nodeIsScienceLayer = L.tree.nodeIsScienceLayer;
import {Geometry, Point} from "ol/geom";
import {Feature} from "ol";
import {Coordinate} from "ol/coordinate";

export interface IPinAttribute {
    layerId: number;
    name: string;
    value: string | string[];
    multi: boolean;
    loading: boolean;
    colour?: string;
}

export interface IPinExtraDataService {
    getAttributes(point: Feature<Point>, layers: Item[]) : IPinAttribute[];
    getOrLoadAttribute(point: Feature<Point>, layer: Item) : IPinAttribute;
}

interface AttributeCache {
    [key: string] : {[key: string] : IPinAttribute};
}

export class PinExtraDataService implements IPinExtraDataService {
    public static $inject = [
        '$http',
        'landcareMapService',
        '$rootScope',
        'portalServices'
    ];

    protected cachedValues : AttributeCache = {};

    public constructor(protected $http: ng.IHttpService,
                       protected mapService: IMapService,
                       protected $rootScope: ng.IRootScopeService,
                       protected portalService: IPortalServices) {

    }

    getCachedAttributes(point: Feature<Geometry>): {[key: string]: any} {
        const k = '_cachedAttributes';

        if (!point.get(k)) {
            point.set(k, {});
        }

        return point.get(k);
    }

    getCachedAttribute(point: Feature<Geometry>, layer: L.tree.Item) {
        let attributes = this.getCachedAttributes(point);
        let layerKey = `${layer.layerId}`;
        if (layerKey in attributes) {
            return attributes[layerKey];
        }

        console.log('getCachedAttribute', layer, attributes);
        return null;
    }

    setAttribute(point: Feature<Geometry>, attr: IPinAttribute) {
        this.getCachedAttributes(point)[`${attr.layerId}`] = attr;
    }

    getOrLoadAttribute(point: Feature<Point>, layer: L.tree.Item): IPinAttribute {
        let cachedResult = this.getCachedAttribute(point, layer);
        if (cachedResult) return cachedResult;

        let result : IPinAttribute = {
            layerId: layer.layerId,
            name: layer.name,
            loading: true,
            value: null,
            multi: false
        };

        this.setAttribute(point, result);

        if (getLayerServiceType(layer) == SERVICE_TYPES.ARC_GIS_FEATURE_SERVICE) {
            this.makeEsriPointQuery(point.getGeometry().getCoordinates(), result, layer.layerService);
            return result;
        }

        const wgs84Coord = transform(point.getGeometry().getCoordinates(), 'EPSG:2193', 'EPSG:4326');

        let layerQuery : IContextLayerQuery;

        try {
            layerQuery = getContextQueryForLayer(layer, this.$http, this.mapService, this.portalService);
        } catch (e) {
            console.warn('failed to construct layer query', result, e);
        }

        if (!layerQuery && nodeIsScienceLayer(layer)) {
            try {
                layerQuery = new PortalsServicesLayerQuery(layer.layerService, this.$http, this.mapService, this.portalService);
            } catch (e) {
                console.warn('failed to construct layer query', result, e);
            }
        }

        if (!layerQuery) {
            console.log('layerQuery is null', result);
            return null;
        }

        const titleProperty: string = layerQuery.titleProperty || '_title';
        result.name = layerQuery.featureName || result.name;

        layerQuery.queryPointProperties(wgs84Coord, [titleProperty])
            .then((data) => {
                if (result.multi) {
                    if (!data.length) {
                        result.value = null;
                        result.multi = false;
                    } else {
                        let alreadyAdded = {};

                        result.value = data.map((d: any) => {
                            let val = d.properties[titleProperty];
                            if (alreadyAdded[val]) return null;
                            alreadyAdded[val] = true;
                            return val;
                        }).filter((d) => d);
                    }
                } else {
                    result.value = data.map((d: any) => d.properties[titleProperty]).join(', ');
                }
                result.loading = false;
            }).catch(() => {
            result.value = '';
            result.loading = false;
        });

        return result;
    }

    getAttributes(point: Feature<Point>, layers: L.tree.Item[]): IPinAttribute[] {
        console.log(layers);
        return layers.map((l) => this.getOrLoadAttribute(point, l));
    }

    private makeEsriPointQuery(coordinate: Coordinate, result: IPinAttribute, layer: LayerService) {
        queryFeatures({
            url: `${layer.url}${layer.params.layers}`,
            returnGeometry: false,
            spatialRel: 'esriSpatialRelIntersects',
            geometry: <IPoint> {
                x: coordinate[0],
                y: coordinate[1],
                spatialReference: {
                    wkid: 2193
                }
            },
            geometryType: "esriGeometryPoint"
        }).then((data: any) => {
            // console.log(feature);
            let applyFn = () => {
                result.loading = false;
                result.multi = true;
                result.value = data.features.map((f) => f.attributes.Name).sort()
            };
            if (this.$rootScope.$$phase) {
                applyFn();
            } else {
                this.$rootScope.$apply(applyFn);
            }
        });

    }
}